import { StreamProtocolClient } from './protocol/stream-protocol';
import { MessageAccumulator } from './message-accumulator';
import { globalChatAPI } from './global-api/global-instance';
import { ERROR_CODES } from './constants/errorCodes';
import { MessagePreprocessorManager } from './preprocessors';
import { defaultPlugins } from './preprocessors/defaultPlugins';
const DEBUG = false;
/**
 * Debug logging helper
 *
 * @param {string} message - The message to log
 * @param {any} data - The data to log
 */
/* v8 ignore start */
const debugLog = (message, data) => {
    if (DEBUG) {
        const timestamp = new Date().toISOString();
        console.log(`[ChatClient ${timestamp}] ${message}`, data || '');
    }
};
/**
 * Handles chat-specific functionality on top of the stream protocol
 */
export class ChatClient {
    /**
     * Constructor for the ChatClient
     *
     * @param {ChatConfig} config - The configuration for the chat client
     * @param {StreamProtocolClient} protocolClient - Optional pre-created protocol client
     */
    constructor(config, protocolClient) {
        this.sessionToken = null;
        this._chatId = null;
        this.connectionId = null;
        this.state = {
            messages: [],
            isTyping: false,
            error: null,
        };
        /**
         * Handles incoming protocol messages
         *
         * @param {StreamMessage} message - The message from the protocol
         */
        this.handleProtocolMessage = (message) => {
            debugLog('Received protocol message', {
                type: message.type,
                messageId: message.messageId,
            });
            switch (message.type) {
                case 'STREAM_DATA':
                    this.handleStreamData(message);
                    break;
                case 'MESSAGE_STARTED':
                    this.handleMessageStarted(message);
                    break;
                case 'MESSAGE_COMPLETED':
                    this.handleMessageCompleted(message);
                    break;
                case 'ERROR':
                    this.handleError(message);
                    break;
                case 'CONNECTION_ESTABLISHED':
                    this.handleConnectionEstablished(message);
                    break;
                default:
                    debugLog('Unknown message type', message);
            }
        };
        /**
         * Handles protocol disconnection
         */
        this.handleDisconnect = () => {
            debugLog('Stream disconnected', {
                chatId: this._chatId,
                hasAccumulatedMessage: this.messageAccumulator.getCurrentMessage() !== null,
            });
            this.messageAccumulator.clear();
            this.setTypingState(false);
            // Do not attempt to reconnect
        };
        /**
         * Generates a connection ID for the chat client
         *
         * @returns {string} - The generated connection ID
         */
        this.generateConnectionId = () => {
            const array = new Uint8Array(16);
            crypto.getRandomValues(array);
            return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
        };
        this.config = config;
        if (!config.agentId) {
            throw new Error('Agent ID is required');
        }
        this.messageAccumulator = new MessageAccumulator();
        this.preprocessorManager = new MessagePreprocessorManager();
        // Add default plugins
        for (const plugin of defaultPlugins) {
            this.preprocessorManager.addPlugin(plugin);
        }
        this.protocol = protocolClient
            ? protocolClient
            : new StreamProtocolClient({
                onMessage: this.handleProtocolMessage,
                onDisconnect: this.handleDisconnect,
                autoReconnect: false, // Always keep reconnect disabled
                /**
                 * Handles errors from the protocol
                 *
                 * @param {EnhancedError} error - The error from the protocol
                 */
                onError: (error) => {
                    var _a, _b;
                    debugLog('Protocol error occurred', error);
                    // Create a more informative error message
                    let errorMessage = error.message;
                    if (error.details && typeof error.details === 'object') {
                        const details = error.details;
                        if (details.state) {
                            errorMessage = `${error.code}: ${error.message} (State: ${details.state})`;
                        }
                        if (details.eventType) {
                            errorMessage += ` [Event: ${details.eventType}]`;
                        }
                    }
                    // Reset state on connection errors
                    if (error.code === ERROR_CODES.CONNECTION_ERROR ||
                        error.code === ERROR_CODES.CONNECTION_CLOSED) {
                        this.setTypingState(false);
                        this.messageAccumulator.clear();
                        // Explicitly disconnect to ensure no reconnection attempts
                        this.protocol.disconnect();
                    }
                    (_b = (_a = this.config).onError) === null || _b === void 0 ? void 0 : _b.call(_a, new Error(errorMessage));
                },
            });
        globalChatAPI.registerChat(config.agentId, this);
    }
    /**
     * Initialize session token
     *
     * @returns {Promise<void>} - A promise that resolves when the session token is initialized
     */
    async initialize() {
        if (!this.sessionToken) {
            // Generate a proper session token using crypto
            const array = new Uint8Array(32);
            crypto.getRandomValues(array);
            this.sessionToken = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
        }
    }
    /**
     * Adds a preprocessor plugin to the chat client
     * @param plugin The preprocessor plugin to add
     */
    addPreprocessorPlugin(plugin) {
        this.preprocessorManager.addPlugin(plugin);
    }
    /**
     * Sends a chat message
     *
     * @param {string} message - The message to send
     * @returns {Promise<string>} - A promise that resolves with the processed message
     */
    async sendMessage(message) {
        var _a, _b;
        if (!this.sessionToken) {
            await this.initialize();
        }
        if (!this.connectionId) {
            this.connectionId = this.generateConnectionId();
        }
        // Process the message through all plugins
        const processedMessage = await this.preprocessorManager.processMessage(message);
        const url = new URL(`${this.config.baseUrl}/api/tenants/${this.config.tenantId}/agents/${this.config.agentId}/chats/stream`);
        // Add query parameters with proper session management
        url.searchParams.append('message', processedMessage);
        if (this._chatId) {
            url.searchParams.append('chatId', this._chatId);
        }
        url.searchParams.append('sessionToken', this.sessionToken);
        url.searchParams.append('connectionId', this.connectionId);
        url.searchParams.append('current_url', window.location.href);
        // Add custom variables and commands to the URL
        const customVariables = (_a = globalChatAPI.getChatControl(this.config.agentId)) === null || _a === void 0 ? void 0 : _a.customVariables;
        debugLog('customVariables: ' + JSON.stringify(customVariables));
        if (customVariables && Object.keys(customVariables).length > 0) {
            debugLog("Adding custom variables to the URL...");
            url.searchParams.append('customVariables', JSON.stringify(customVariables));
        }
        const commands = (_b = globalChatAPI.getChatControl(this.config.agentId)) === null || _b === void 0 ? void 0 : _b.commands;
        if (commands && Object.keys(commands).length > 0) {
            url.searchParams.append('commands', JSON.stringify(commands));
        }
        debugLog('Sending message to stream endpoint', {
            url: url.toString(),
            message: processedMessage,
            tenantId: this.config.tenantId,
            agentId: this.config.agentId,
            sessionToken: this.sessionToken,
            chatId: this._chatId,
            customVariables,
        });
        // Set typing state to true when sending message
        this.setTypingState(true);
        // Connect to the stream
        try {
            this.protocol.connect(url.toString());
            debugLog('Successfully connected to stream');
        }
        catch (error) {
            debugLog('Failed to connect to stream', error);
            throw error;
        }
        return processedMessage;
    }
    /**
     * Starts a new chat session
     */
    startNewChat() {
        this.messageAccumulator.clear();
        this.protocol.disconnect();
        this.setTypingState(false);
        // Reset chat ID to start a new chat
        this._chatId = null;
    }
    /**
     * Returns the current chat ID
     *
     * @returns {string | null} - The current chat ID
     */
    get chatId() {
        return this._chatId;
    }
    /**
     * Handles stream data messages
     */
    handleStreamData(message) {
        var _a, _b;
        debugLog('Processing stream data', {
            messageId: message.messageId,
            role: message.data.role,
            isLast: message.data.isLast,
            chatId: message.data.chatId,
            contentLength: message.data.content.length,
        });
        // Set typing state to false when receiving messages
        this.setTypingState(false);
        // Store chat ID if this is the first message and it contains a chat ID
        if (!this._chatId && message.data.chatId) {
            this._chatId = message.data.chatId;
            debugLog('Received chat ID from backend', { chatId: this._chatId });
        }
        // Let accumulator handle the message
        const chatMessage = this.messageAccumulator.append(message);
        debugLog('Message accumulated', {
            messageId: chatMessage.id,
            role: chatMessage.role,
            contentLength: chatMessage.content.length,
        });
        // Send message update
        (_b = (_a = this.config).onMessage) === null || _b === void 0 ? void 0 : _b.call(_a, chatMessage);
        // If this is the last message, stop typing and disconnect
        if (message.data.isLast) {
            debugLog('Received last message flag, disconnecting');
            this.setTypingState(false);
            this.protocol.disconnect();
        }
    }
    /**
     * Handles error messages from the stream
     */
    handleError(message) {
        var _a, _b;
        debugLog('Handling error message', {
            code: message.data.code,
            message: message.data.message,
            details: message.data.details,
        });
        this.setTypingState(false);
        // Disconnect on error to prevent reconnection attempts
        this.protocol.disconnect();
        (_b = (_a = this.config).onError) === null || _b === void 0 ? void 0 : _b.call(_a, new Error(message.data.message));
    }
    /**
     * Updates typing state and notifies listeners
     */
    setTypingState(isTyping) {
        if (this.state.isTyping !== isTyping) {
            debugLog('Typing state changed', {
                isTyping,
                chatId: this._chatId || 'not_set',
            });
            this.state.isTyping = isTyping;
            // Ensure the callback is called in the next tick to avoid React state update conflicts
            setTimeout(() => {
                var _a, _b;
                (_b = (_a = this.config).onTypingStateChange) === null || _b === void 0 ? void 0 : _b.call(_a, isTyping);
            }, 0);
        }
    }
    /**
     * Handles message started events
     */
    handleMessageStarted(message) {
        var _a, _b, _c, _d;
        debugLog('Message started', message);
        const chatMessage = this.messageAccumulator.handleMessageStarted(message);
        (_b = (_a = this.config).onChatStarted) === null || _b === void 0 ? void 0 : _b.call(_a, this._chatId || '');
        (_d = (_c = this.config).onMessage) === null || _d === void 0 ? void 0 : _d.call(_c, chatMessage);
    }
    /**
     * Handles message completed events
     */
    handleMessageCompleted(message) {
        var _a, _b;
        debugLog('Message completed', message);
        const chatMessage = this.messageAccumulator.updateMessageId(message);
        if (chatMessage) {
            (_b = (_a = this.config).onMessage) === null || _b === void 0 ? void 0 : _b.call(_a, chatMessage);
        }
    }
    /**
     * Handles connection established events
     */
    handleConnectionEstablished(message) {
        var _a, _b;
        debugLog('Connection established', message);
        if (message.data.chatId) {
            this._chatId = message.data.chatId;
            (_b = (_a = this.config).onChatStarted) === null || _b === void 0 ? void 0 : _b.call(_a, this._chatId || '');
            debugLog('Received chat ID from backend', { chatId: this._chatId });
        }
    }
}
//# sourceMappingURL=chat-client.js.map